import { useQuery, useSubscription } from '@apollo/client';
import classNames from 'classnames';
import _ from 'lodash';
import Quill, { Delta } from 'quill/core';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { ORGANIZATION_ALLOWED_EMBED_HOSTS_QUERY } from '../admin/security/AllowedIframeHostsSettings';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import Toast from '../components/Toast';
import {
  ACTION_EDITOR_RENDER_DURATION,
  ACTION_TYPING_APDEX,
  CATEGORY_PRODUCT,
} from '../constants/analytics';
import useQuillOnWindowInDev from '../dev/useQuillOnWindowInDev';
import type {
  PerformanceMeasureHandler,
  TextChangeHandler,
} from '../editor/Editor';
import Editor from '../editor/Editor';
import { useQuill, withQuillContextProvider } from '../editor/QuillContext';
import UIElementResizeContext from '../editor/UIElementResizeContext';
import WiderLineSizeContext from '../editor/WiderLineSizeContext';
import { EditorActions } from '../editor/editorSlice';
import { gql } from '../graphql/generated/gql';
import { useUpdateLocalViewedPost } from '../home/useViewedPostsColumn';
import { getSearchContext } from '../search/searchContext';
import { usePostSeries } from '../series/useAllSeries';
import PostModeToggle from '../topbar/PostModeToggle';
import PostStatus from '../topbar/PostStatus';
import { useSavePostToRecentlyViewed } from '../topbar/SearchBar';
import UIActions from '../ui/UIActions';
import Logger from '../utils/Logger';
import PageMeta from '../utils/PageMeta';
import { track } from '../utils/analytics';
import useErrorRedirectEffect from '../utils/error/useErrorRedirectEffect';
import { isFeatureEnabled } from '../utils/features';
import Sizes from '../utils/responsive/Sizes';
import { useIsSize } from '../utils/responsive/isSize';
import useMainContentHistory from '../utils/router/useMainContentHistory';
import useNavigate from '../utils/router/useNavigate';
import useSlug from '../utils/router/useSlug';
import MagneticWall, {
  getMagneticWallItemElementsWithIds,
} from './MagneticWall';
import NewPostStickyControl from './NewPostStickyControl';
import PostActivityTracker from './PostActivityTracker';
import PostContributors from './PostContributors';
import PostFooter from './PostFooter';
import PostLayoutContainer, {
  PostLayoutAbove,
  PostLayoutBelow,
  PostLayoutEditor,
  PostLayoutMagneticWall,
  PostLayoutTOC,
} from './PostLayoutContainer';
import PostLayoutProvider, {
  getPostLayoutSizeFromWidth,
} from './PostLayoutProvider';
import PostSaveTracker from './PostSaveTracker';
import PostSeriesBar from './PostSeriesBar';
import PostSkeleton from './PostSkeleton';
import PostStateBar from './PostStateBar';
import PostStickyControl from './PostStickyControl';
import { PostTOCProvider, usePostTOC } from './PostTOCContext';
import styles from './PostView.module.scss';
import PostViewBannerEditor from './PostViewBannerEditor';
import PostActionButtons from './actionButtons/PostActionButtons';
import getVerificationStatus, {
  VerificationStatus,
} from './getVerificationStatus';
import {
  isVerifier,
  PostPolicyAction,
  usePostPolicyCheck,
} from './postPolicyCheck';
import PostSeriesFooter from './series/PostSeriesFooter';
import PostTopicBar from './topic/PostTopicBar';
import { isDeletedLocally } from './useDeletePost';
import useInactivityObserver from './useInactivityObserver';
import usePostChromeVisible from './usePostChromeVisible';

const isNewPostLayout = isFeatureEnabled('new_post_layout');

const POST_QUERY = gql(`
  query PostView(
    $id: ID!
    $queryId: String
    $resultId: String
    $position: Int
  ) {
    post(
      id: $id
      view: true
      queryId: $queryId
      resultId: $resultId
      position: $position
    ) {
      id
      threads {
        id
      }
      annotations {
        id
      }
      series {
        id
        ...PostSeriesFooter_series
      }
      # TODO: Fix this the next time the file is edited.
      # eslint-disable-next-line @graphql-eslint/require-id-when-available
      externalPost {
        externalId
      }
      banner {
        original
      }
      verificationEntries {
        id
        verifier {
          id
          name
        }
      }
      ...PostTopicBar_post
      # TODO: Fix this the next time the file is edited.
      # eslint-disable-next-line @graphql-eslint/require-id-when-available
      ...Editor_post
      ...usePostPolicyCheck_post
      ...MagneticWall_post
      ...useSavePostToRecentlyViewed_post
      # TODO: Fix this the next time the file is edited.
      # eslint-disable-next-line @graphql-eslint/require-id-when-available
      ...PostViewBannerEditor_post
      ...useUpdateLocalViewedPost_post
      ...EditorActions_post
      ...getVerificationStatus_post
      ...usePostSeries_post
      ...isVerifier_post
    }
  }
`);

const POST_UPDATED_SUBSCRIPTION = gql(`
  subscription postUpdate($postId: ID!) {
    postUpdated(postId: $postId) {
      id
      publishedAt
      archivedAt
    }
  }
`);

// eslint-disable-next-line slab-graphql/no-unused-colocated-fragments
const POST_DELETED_SUBSCRIPTION = gql(`
  subscription postDeleted($postId: ID!) {
    post: postDeleted(postId: $postId) {
      ...PostSummaryFields
    }
  }
`);

const MAX_TRACKING_EVENT_COUNT = 20;
const useTypingPerformanceObserver = (postId: string) => {
  const { quill } = useQuill();
  const [shouldObserve, setShouldObserve] = useState(true);

  useEffect(() => {
    if (!quill || !shouldObserve) return undefined;

    const isEventTimingAPISupported =
      'PerformanceObserver' in window &&
      typeof PerformanceObserver === 'function' &&
      PerformanceObserver.supportedEntryTypes?.includes('event');

    if (!isEventTimingAPISupported) return undefined;

    // We use timestamp for event identifier to link a PerformanceObserver entry to a keydown event
    // https://github.com/w3c/event-timing/issues/108
    const eventTimeStamps = new Set<number>();
    const slowDurations: number[] = [];
    const alphanumericRegex = /^[a-zA-Z0-9]$/;

    let lastObserverEntryWaitingTimer: ReturnType<typeof setTimeout> | null =
      null;

    const handleKeydown = (e: KeyboardEvent) => {
      if (lastObserverEntryWaitingTimer) return;

      // Don't track shortcuts or Enter/Backspace keys because their performance varies a lot and
      // can make the Apdex less accurate.
      const shouldTrackPerformance =
        !e.isComposing &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !e.repeat &&
        alphanumericRegex.test(e.key);

      if (!shouldTrackPerformance) {
        return;
      }

      if (eventTimeStamps.size < MAX_TRACKING_EVENT_COUNT) {
        eventTimeStamps.add(e.timeStamp);
        return;
      }

      // Wait for PerformanceObserver to collect data for the last keydown event
      lastObserverEntryWaitingTimer = setTimeout(() => {
        setShouldObserve(false); // We already have enough data

        const frustratedDuration = 400;
        const frustratedCount = slowDurations.filter(
          (d) => d >= frustratedDuration,
        ).length;
        const toleratingCount = slowDurations.length - frustratedCount;
        const satisfiedCount = MAX_TRACKING_EVENT_COUNT - slowDurations.length;
        const apdex =
          (satisfiedCount + toleratingCount / 2) / MAX_TRACKING_EVENT_COUNT;

        track(ACTION_TYPING_APDEX, {
          category: CATEGORY_PRODUCT,
          label: postId,
          value: apdex,
        });

        if (apdex < 0.5) {
          Logger.warn('Slow typing warning', { postId, apdex });
        }
      }, 2000);
    };
    quill.root.addEventListener('keydown', handleKeydown);

    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.name === 'keydown' && eventTimeStamps.has(entry.startTime)) {
          slowDurations.push(entry.duration);
        }
      });
    });

    observer.observe({ type: 'event' });
    return () => {
      quill.root.removeEventListener('keydown', handleKeydown);
      observer.disconnect();
      if (lastObserverEntryWaitingTimer) {
        clearTimeout(lastObserverEntryWaitingTimer);
      }
    };
  }, [quill, shouldObserve, postId]);
};

const usePostDeleteSubscription = (postId: string) => {
  const dispatch = useAppDispatch();
  const mainContentHistory = useMainContentHistory();
  const handleSubscriptionData = () => {
    if (isDeletedLocally(postId)) return;

    dispatch(UIActions.removeFromRecentlyViewedPostIds(postId));
    dispatch(
      UIActions.pushToast((toastProps: { onDismiss: () => void }) => (
        <Toast
          level="error"
          message="The post you were viewing was just deleted."
          onDismiss={toastProps.onDismiss}
          title="Oops"
        />
      )),
    );
    setTimeout(() => {
      mainContentHistory.back((route) => !route.pathname.includes(postId), {
        replace: true,
      });
    }, 0);
  };

  useSubscription(POST_DELETED_SUBSCRIPTION, {
    variables: { postId },
    onData: handleSubscriptionData,
  });
};

const PostView = () => {
  const dispatch = useAppDispatch();
  const [postId] = useSlug('postSlug');

  useEffect(
    () => () => {
      dispatch(EditorActions.leave());
    },
    [dispatch, postId],
  );

  const isMobile = useIsSize(Sizes.SM);
  const isChromeVisible = usePostChromeVisible({ enabled: isMobile });
  const { quill } = useQuill();
  useQuillOnWindowInDev();
  useTypingPerformanceObserver(postId);

  const readOnly = useAppSelector((state) => state.editor.readOnly);
  const title = useAppSelector((state) => state.editor.title);
  const isVersionLoaded = useAppSelector((state) => state.editor.version > -1);

  const isLocked = useAppSelector(
    (state) => state.session.organization.isLocked,
  );
  const currentUser = useAppSelector((state) => state.session.user);
  const channel = useAppSelector((state) => state.editor.channel);
  useInactivityObserver(channel);

  const { queryId, resultId, position, highlightedRangeIndex } = useMemo(
    () => getSearchContext(postId),
    [postId],
  );

  const postLayoutContainerRef = useRef<HTMLElement | null>(null);
  const postLayoutBannerRef = useRef<HTMLDivElement | null>(null);

  // Remote updates not applied yet
  const receivedRef = useRef(new Delta());

  useEffect(
    () => () => {
      dispatch(UIActions.closeRightPanel(true));
    },
    [dispatch, postId],
  );

  const { data, error, loading } = useQuery(POST_QUERY, {
    fetchPolicy: 'network-only',
    variables: { id: postId, queryId, resultId, position },
  });

  const { post } = data || {};

  useUpdateLocalViewedPost(post?.id);

  usePostDeleteSubscription(postId);
  useSubscription(POST_UPDATED_SUBSCRIPTION, { variables: { postId } });

  const { data: allowedEmbedHostsData } = useQuery(
    ORGANIZATION_ALLOWED_EMBED_HOSTS_QUERY,
  );
  const allowedEmbedHosts = useMemo(
    () =>
      _.compact(
        allowedEmbedHostsData?.session?.organization?.settings
          ?.allowedEmbedHosts,
      ),
    [allowedEmbedHostsData],
  );

  useErrorRedirectEffect({ error, noun: 'post' });

  useSavePostToRecentlyViewed(post);

  const quillRef = useRef(quill);
  useEffect(() => {
    if (!quillRef.current && quill) {
      if (document.location.hash) {
        quill
          .getModule('scrollToAnchor')
          // @ts-expect-error
          ?.scrollToAnchor(document.location.hash);
      }
    }

    quillRef.current = quill;
    return () => {
      quillRef.current = null;
    };
  }, [quill]);

  const series = usePostSeries(post);

  const postPolicyCheck = usePostPolicyCheck();

  const isPostEditable =
    !!post &&
    !isLocked &&
    postPolicyCheck(PostPolicyAction.EditPost, post, currentUser) &&
    post.externalPost == null;

  const loadedPostIdRef = useRef<string | null>(null);
  const loadedIsPostEditableRef = useRef<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();

  const verificationStatus = post ? getVerificationStatus(post) : null;

  const isCurrentUserVerifier = post ? isVerifier(post, currentUser) : false;

  useEffect(() => {
    const { verification, ...locationState } = location.state ?? {};
    if (verification && verificationStatus) {
      const verifier = post?.verificationEntries[0]?.verifier;
      if (
        verifier &&
        verifier.id !== currentUser.id &&
        isCurrentUserVerifier &&
        verificationStatus === VerificationStatus.Verified
      ) {
        dispatch(
          UIActions.pushToast({
            level: 'success',
            message: `${verifier.name} has already verified this post!`,
          }),
        );
      }
      navigate(location, { replace: true, state: locationState });
    }
  }, [
    currentUser.id,
    dispatch,
    isCurrentUserVerifier,
    location,
    navigate,
    post?.verificationEntries,
    verificationStatus,
  ]);

  useEffect(() => {
    if (
      post &&
      !error &&
      !loading &&
      (loadedPostIdRef.current !== post.id ||
        loadedIsPostEditableRef.current !== isPostEditable)
    ) {
      dispatch(
        EditorActions.load({
          post,
          isEditable: isPostEditable,
          isDefaultReadOnly:
            isFeatureEnabled('new_post_layout') &&
            getPostLayoutSizeFromWidth(window.innerWidth) === 'mobile',
          onRemoteDelta: (delta: Delta) => {
            receivedRef.current = receivedRef.current.compose(delta);
            quillRef.current?.updateContents(
              receivedRef.current,
              Quill.sources.API,
            );
          },
          navigate,
        }),
      );

      if (location.state?.edit) {
        dispatch(EditorActions.readOnly(false));
        if (location.state?.editTopic) {
          // Timeout is needed when the editor changes to editing state to focus the topic input.
          setTimeout(() => {
            dispatch(UIActions.changePostTopicSelectOpen(true));
          }, 1);
        }
        navigate(location, {
          replace: true,
          state: { edit: false, editTopic: false },
        });
      }

      loadedPostIdRef.current = post.id;
      loadedIsPostEditableRef.current = isPostEditable;
    }

    return undefined;
  }, [dispatch, error, isPostEditable, loading, navigate, post, location]);

  const handleTextChange: TextChangeHandler = useCallback(
    (_delta, _oldContent, source) => {
      if (source === Quill.sources.API) {
        receivedRef.current = new Delta();
      }
    },
    [],
  );

  const handlePerformanceMeasure: PerformanceMeasureHandler = useCallback(
    (durations) => {
      track(ACTION_EDITOR_RENDER_DURATION, {
        category: CATEGORY_PRODUCT,
        label: postId,
        ...durations,
      });
    },
    [postId],
  );

  const currentBannerUrl = post?.banner?.original ?? null;
  const currentPostBanner = useAppSelector(
    (state) => state.editor.currentPostBanner,
  );
  const hasStateBar = useAppSelector((state) => state.editor.hasStateBar);

  const banner =
    currentPostBanner ??
    (currentBannerUrl ? { image: currentBannerUrl } : null);

  const postLayoutTocRef = useRef<HTMLElement | null>(null);
  const postLayoutMagneticWallRef = useRef<HTMLElement | null>(null);
  const [gridItemForStateBar, setGridItemForStateBar] =
    useState<HTMLElement | null>(null);

  const isEditing =
    !readOnly ||
    (post != null &&
      !isLocked &&
      postPolicyCheck(PostPolicyAction.EditPost, post, currentUser) &&
      post.externalPost != null);

  const hasMagneticWallItems = useMemo(() => {
    const hasThreadsOrAnnotations =
      (!!post?.threads && post?.threads?.length > 0) ||
      (!!post?.annotations && post?.annotations?.length > 0);
    return quill && hasThreadsOrAnnotations
      ? getMagneticWallItemElementsWithIds(quill.container).length > 0
      : false;
  }, [quill, post?.threads, post?.annotations]);

  return (
    <PostTOCProvider>
      <PostLayoutProvider
        hasMagneticWallItems={hasMagneticWallItems}
        post={post}
      >
        <div className={styles.container}>
          {post && gridItemForStateBar && (
            <PostStateBar
              gridItem={gridItemForStateBar}
              postId={post.id}
              hidden={!isChromeVisible}
              tocContainerRef={postLayoutTocRef}
              magneticWallContainerRef={postLayoutMagneticWallRef}
            />
          )}
          <PageMeta title={`${title || 'Post'} - Slab`} />
          <PostLayoutContent
            postId={postId}
            hasBanner={banner != null}
            isChromeVisible={isChromeVisible}
          >
            <PostLayoutContainer
              ref={postLayoutContainerRef}
              hasBanner={banner != null}
              hasStateBar={hasStateBar}
              hasMagneticWallItems={hasMagneticWallItems}
            >
              {isVersionLoaded && post != null ? (
                <WiderLineSizeContext
                  containerRef={postLayoutContainerRef}
                  editor={quill?.scroll.domNode}
                >
                  <UIElementResizeContext>
                    <PostLayoutAbove ref={setGridItemForStateBar}>
                      <PostTopicBar post={post} editing={isEditing} />
                      {series && (
                        <PostSeriesBar postId={post.id} series={series} />
                      )}
                    </PostLayoutAbove>
                    {banner && (
                      <PostViewBannerEditor
                        setPostLayoutBannerRef={postLayoutBannerRef}
                        banner={banner}
                        currentBannerUrl={currentBannerUrl}
                        post={post}
                      />
                    )}
                    <PostLayoutEditor>
                      <PostActivityTracker
                        postId={postId}
                        queryId={queryId}
                        resultId={resultId}
                      />
                      <PostSaveTracker />
                      <Editor
                        allowedEmbedHosts={allowedEmbedHosts}
                        post={post}
                        highlightedRangeIndex={highlightedRangeIndex}
                        onTextChange={handleTextChange}
                        onPerformanceMeasure={handlePerformanceMeasure}
                        hasBanner={banner != null}
                      />
                    </PostLayoutEditor>

                    {isNewPostLayout ? (
                      <PostLayoutTOC ref={postLayoutTocRef}>
                        <NewPostStickyControl
                          postId={postId}
                          postLayoutBannerRef={postLayoutBannerRef}
                          postLayoutContainerRef={postLayoutContainerRef}
                          hasBanner={banner != null}
                        />
                      </PostLayoutTOC>
                    ) : (
                      <PostStickyControl
                        postId={postId}
                        postLayoutBannerRef={postLayoutBannerRef}
                      />
                    )}

                    <PostLayoutMagneticWall ref={postLayoutMagneticWallRef}>
                      {quill && (
                        <MagneticWall
                          quill={quill}
                          post={post}
                          canEditPost={isPostEditable}
                        />
                      )}
                    </PostLayoutMagneticWall>
                    <PostLayoutBelow>
                      {series && (
                        <div className={styles.postSeriesBarContainer}>
                          <PostSeriesFooter postId={postId} series={series} />
                          <PostSeriesBar postId={post.id} series={series} />
                        </div>
                      )}
                      <PostContributors postId={post.id} />
                    </PostLayoutBelow>
                  </UIElementResizeContext>
                </WiderLineSizeContext>
              ) : (
                <>
                  <PostLayoutAbove />
                  <PostLayoutEditor>
                    <div className="post-layout-editor-inner">
                      <PostSkeleton />
                    </div>
                  </PostLayoutEditor>
                </>
              )}
            </PostLayoutContainer>
          </PostLayoutContent>
        </div>
      </PostLayoutProvider>
    </PostTOCProvider>
  );
};

interface PostLayoutContentProps {
  hasBanner: boolean;
  isChromeVisible: boolean;
  postId: string;
  children: React.ReactNode;
}

const PostLayoutContent = ({
  isChromeVisible,
  hasBanner,
  postId,
  children,
}: PostLayoutContentProps) => {
  const { isTOCOpen } = usePostTOC();
  const hasStateBar = useAppSelector((state) => state.editor.hasStateBar);
  const isRightPanelOpen = useAppSelector((state) => state.ui.rightPanel.open);
  const isSidebarOpen = useAppSelector((state) => state.ui.isSidebarOpen);

  if (isNewPostLayout) {
    return (
      <div id="content" className="no-padding-x no-padding-y">
        {children}
      </div>
    );
  }

  return (
    <div
      id="content"
      className={classNames('no-padding-x', 'no-padding-y', 'post-container', {
        'hide-ui-chrome': !isChromeVisible,
        'has-state-bar': hasStateBar,
        'show-right-panel': isRightPanelOpen,
        'show-sidebar': isSidebarOpen,
        'show-sticky-toc': isTOCOpen,
        'has-banner': hasBanner,
      })}
    >
      {children}
      <PostFooter>
        <PostModeToggle />
        <div className="post-status">
          <PostStatus />
        </div>
        <PostActionButtons horizontal postId={postId} />
      </PostFooter>
    </div>
  );
};

export default withQuillContextProvider(PostView);
